import { neutral } from "@flat-ui";
import { Box, Divider, Grid2 } from "@mui/material";
import {
  DEFAULT_FONT_FAMILY,
  withEllipsisThreeLines,
} from "@styles/common-styles";
import { PropsWithChildren } from "react";

interface Props {
  /** Text to show on the left side of the container header */
  title: string;

  /** Content to show next to the title (can be text or links etc.) */
  description?: React.ReactNode;

  /** Action buttons to show on the right side of the container header */
  actionButtons?: JSX.Element;

  /** ID to identify the container. It is used for our automation framework playwright */
  dataTestId?: string;
}

/**
 * A page container/wrapper with a header section where a title and description can be displayed on the left
 * and actions buttons can be displayed on the right.
 */
export function PageContainer({
  children,
  title,
  description,
  actionButtons,
  dataTestId,
}: PropsWithChildren<Props>): JSX.Element {
  return (
    <Box component="div" sx={{ width: "100%" }} data-testid={dataTestId}>
      <Grid2
        container
        sx={{
          boxShadow: `0 1px 0 0 ${neutral[200]}`,
          paddingTop: 2,
        }}
      >
        <Grid2
          flexWrap="nowrap"
          container
          sx={{
            minHeight: "46px",
            display: "flex",
            alignItems: "center",
            fontSize: "14px",
            color: neutral[800],
          }}
          size={
            actionButtons
              ? {
                  xs: 12,
                  sm: 12,
                  md: 6,
                  lg: 7,
                  xl: 8,
                }
              : // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                12
          }
        >
          <Grid2
            sx={{
              fontFamily: DEFAULT_FONT_FAMILY,
              fontSize: "14px",
              color: neutral[800],
            }}
          >
            {title}
          </Grid2>

          <Grid2 sx={{ ml: "35px", ...withEllipsisThreeLines }}>
            {description}
          </Grid2>
        </Grid2>

        {actionButtons && (
          <Grid2
            sx={{
              minHeight: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              svg: { height: "24px", width: "24px" },
            }}
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 5,
              xl: 4,
            }}
          >
            {actionButtons}
          </Grid2>
        )}
      </Grid2>
      <Divider />
      <Box
        component="div"
        sx={{
          width: "100%",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
